import React, {useRef, useState} from 'react';
import {
  MediaFile,
  VimeoMediaFile,
} from '../../../../data/models/LibraryFile/MediaFile';
import {addVideoSourceWizardText} from '../addVideoSourceWizardText';
import {
  getMilisecondsFromTimeText,
  getTimeMilisecondsTextFromSeconds,
} from '../../../../utils/getTimeTextFromSeconds';
import SimplePlayerControlled from '../../../../uiToolkit/SimplePlayerControlled';
import './styles.css';
import TimeInput from '../../../../uiToolkit/Inputs/TimeInput/indes';
import Button from '../../../../uiToolkit/Buttons/Button';
import ModalContainer from '../../../../uiToolkit/ModalContainer';

interface Props {
  selectedFile?: MediaFile;
  selectedVimeoFile?: VimeoMediaFile;
  sourceUrl?: string;
  onCancel: () => void;
  onSave: (start: number, end: number) => void;
  isLoading: boolean;
  showRemoveAudio?: boolean;
  removeAudio?: boolean;
  onRemoveAudioChange?: (value: boolean) => void;
  removeAudioLabel?: string;
  saveLabel?: string;
}

const CropVideoStepUI = React.memo(
  ({
    selectedFile,
    onCancel,
    onSave,
    selectedVimeoFile,
    isLoading,
    sourceUrl,
    showRemoveAudio = false,
    removeAudio = false,
    onRemoveAudioChange,
    removeAudioLabel,
    saveLabel,
  }: Props) => {
    const [width, setWidth] = useState(0);
    const [timeStart, setTimeStart] = useState('00:00:00.000');
    const [timeEnd, setTimeEnd] = useState('00:00:01.000');
    const [duration, setDuration] = useState(0);

    const containerRef = useRef<any>(null);

    const saveNode = () => {
      if (startTimeIsValid() && endTimeIsValid()) {
        onSave(
          getMilisecondsFromTimeText(timeStart),
          getMilisecondsFromTimeText(timeEnd),
        );
      }
    };

    const onSaveFull = () => {
      onSave(0, duration);
    };

    React.useEffect(() => {
      function handleResize() {
        if (containerRef && containerRef.current) {
          setWidth(containerRef.current.offsetWidth);
        }
      }

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    React.useLayoutEffect(() => {
      if (containerRef && containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    }, []);

    const startTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeStart);
        const endSeconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration && seconds < endSeconds;
      }
      return true;
    };

    const endTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration;
      }
      return true;
    };

    const onDuration = (dur: number) => {
      setDuration(dur);
      setTimeEnd(getTimeMilisecondsTextFromSeconds(dur));
    };

    return (
      <ModalContainer onClose={onCancel}>
        <div className="AddVideoSourceStepContent CropPlayerContent">
          <p className="CropHeader">{addVideoSourceWizardText.cropTitle}</p>
          <div className="CropPlayerContainer" ref={containerRef}>
            <SimplePlayerControlled
              width={width}
              onDuration={onDuration}
              showFullscreen
              source={
                sourceUrl ||
                (selectedFile ? selectedFile?.fileUrl : selectedVimeoFile?.url)
              }
              startTime={
                startTimeIsValid()
                  ? getMilisecondsFromTimeText(timeStart)
                  : undefined
              }
              endTime={
                endTimeIsValid()
                  ? getMilisecondsFromTimeText(timeEnd)
                  : undefined
              }
            />
          </div>
          <div className="TimePickersContainer">
            <TimeInput
              value={timeStart}
              onChange={setTimeStart}
              label={addVideoSourceWizardText.startTime}
              isValid={startTimeIsValid()}
              labelTop
              includeMiliseconds
            />
            <TimeInput
              value={timeEnd}
              onChange={setTimeEnd}
              label={addVideoSourceWizardText.endTime}
              isValid={endTimeIsValid()}
              labelTop
              includeMiliseconds
            />
          </div>
          <div className="UseFullVideoContainer">
            <button onClick={onSaveFull}>
              {addVideoSourceWizardText.useFullVideo}
            </button>
          </div>
          {showRemoveAudio && onRemoveAudioChange && (
            <label className="RemoveAudioOption">
              <input
                type="checkbox"
                checked={removeAudio}
                onChange={e => onRemoveAudioChange(e.target.checked)}
              />
              <span>
                {removeAudioLabel || addVideoSourceWizardText.removeAudio}
              </span>
            </label>
          )}
          <div className="Footer">
            <Button
              title={addVideoSourceWizardText.cancel}
              onClick={onCancel}
              uiType="text"
            />
            <Button
              title={saveLabel || addVideoSourceWizardText.save}
              onClick={saveNode}
              disabled={!startTimeIsValid() || !endTimeIsValid() || isLoading}
              uiType="action"
            />
          </div>
        </div>
      </ModalContainer>
    );
  },
);

export default CropVideoStepUI;
